import { useTranslation } from "react-i18next";
import type { FacilityNode } from "@arc/contracts";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import type { AuthSession } from "../../auth/keycloak.js";
import { useFacilityStats } from "./useFacilityMutations.js";
import { lifecycleConfirmMessage, duplicateConfirmMessage } from "./facility-confirm-copy.js";

export type SubtreeConfirmKind = "archive" | "unarchive" | "duplicate";

const TITLE = {
  archive: "manualManagement.subtree.archive",
  unarchive: "manualManagement.subtree.unarchive",
  duplicate: "manualManagement.subtree.duplicate",
} as const satisfies Record<SubtreeConfirmKind, string>;
const LABEL = {
  archive: "facilityManagement.actions.archive",
  unarchive: "facilityManagement.actions.unarchive",
  duplicate: "other.duplicate",
} as const satisfies Record<SubtreeConfirmKind, string>;

export interface SubtreeConfirmDialogProps {
  kind: SubtreeConfirmKind;
  node: FacilityNode;
  session: AuthSession | null;
  /** The mutation is in flight. */
  isPending: boolean;
  /** Wording overrides. The two realm styles disagree on the unarchive verb — the drill-down says "Unarchive", the
   *  classic list says "Restore" — and #108 is a COUNT fix, so neither is quietly renamed to match the other. */
  title?: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The confirmation for the three operations that cascade over a facility's WHOLE subtree — archive, unarchive and
 * duplicate (#108).
 *
 * It exists so the number in that sentence is the **server's**. The count used to be derived from the client's
 * facility tree, which is permission-filtered: a scoped `access_manage` caller was told "and its 2 subfacilities"
 * while the server cascaded over seven. Here the count comes from `GET /facilities/:id/stats` (`descendants`), the
 * same unfiltered subtree the operation itself walks.
 *
 * Reading it costs no extra permission: stats and all three operations gate on the identical `requireManageSubtree`
 * (`facilities.service.ts:401` archive/unarchive · `:535` stats · `:667` duplicate), so a caller who can press the
 * button can always read the count.
 *
 * **While the count is loading the primary is disabled** (`confirmDisabled`, NOT `isPending` — Cancel and dismissal
 * stay live): confirming a cascade whose size is still unknown is the very thing being fixed. If stats FAILS the copy
 * drops the number rather than inventing one, and the action is re-enabled — the operation is reversible, the server
 * authorizes independently, and a transient read failure should not strand someone who may legitimately archive.
 *
 * That gate reads **`isLoading`, never `isPending`** (diff panel). `useFacilityStats` is `enabled: session !== null`,
 * and a DISABLED react-query query reports `isPending: true` forever while never fetching — measured:
 * `disabled → isPending true, isFetching false, isLoading false`. Gating on `isPending` would therefore leave the
 * confirm button permanently dead for a null session, with no way out but Cancel. `isLoading` (= pending AND
 * fetching) is true only while a request is genuinely in flight, which is the condition actually meant here.
 */
export function SubtreeConfirmDialog({ kind, node, session, isPending, title, confirmLabel, onConfirm, onCancel }: SubtreeConfirmDialogProps) {
  const { t } = useTranslation();
  const stats = useFacilityStats(session, node.id);
  // `descendants` excludes the root, which is exactly what the copy wants. `null` ⇒ unknown (loading or failed).
  const descendants = stats.data ? stats.data.descendants : null;

  return (
    <ConfirmDialog
      isOpen
      title={title ?? t(TITLE[kind])}
      message={kind === "duplicate" ? duplicateConfirmMessage(node, descendants, t) : lifecycleConfirmMessage(kind, node, descendants, t)}
      confirmLabel={confirmLabel ?? t(LABEL[kind])}
      isPending={isPending}
      confirmDisabled={stats.isLoading}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
